import { motion } from "framer-motion";
import { FaAppStoreIos, FaGooglePlay } from "react-icons/fa";
import { Cashier, RestaurantInvoiceProps } from "../../types/restaurant";
import { Button } from "../Button";
import { SaveInvoiceFromCashier } from "../models/SaveInvoiceFromCashier";
import {
  formatDate,
  formatInvoiceDate,
  formatInvoiceTime,
} from "../../utils/dateFormat";
import shield from "../../images/shield.svg";
import { QRCodeCanvas } from "qrcode.react";
import SaudiRiyal from "../../images/icons/saudi-riyal-symbol.svg";

const RestaurantInvoice = ({
  invoice,
  restaurantId,
  cashierId,
  onSave,
  setSelectedCashier,
  type,
}: {
  invoice: RestaurantInvoiceProps;
  restaurantId?: number;
  cashierId?: string;
  onSave?: () => void;
  setSelectedCashier?: (cashier: Cashier | null) => void;
  type: "cashier" | "web" | "user" | "search";
}) => {
  return (
    <div className="flex flex-col items-center">
      {(type === "web" || type === "user") && (
        <p className="text-primaryText text-center text-2xl md:text-3xl font-bold mt-10 mb-4">
          طلبك عندك{" "}
          <span className="text-primary glow-text text-3xl md:text-4xl">
            وفاتورتك
          </span>{" "}
          عندنا
        </p>
      )}

      <motion.div
        initial={{ opacity: 0, y: -100 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 2, ease: "easeInOut" }}
        className="w-full md:!w-96 h-fit my-4 md:my-10 invoice-shadow relative"
      >
        <motion.div
          initial={{ clipPath: "inset(0 0 100% 0)" }}
          animate={{ clipPath: "inset(0 0 0% 0)" }}
          transition={{ duration: 2, ease: "easeInOut" }}
          className="relative text-sm flex flex-col justify-center items-center w-full h-fit pt-16 pb-6 px-7  bg-white rounded-xl overflow-hidden"
        >
          <img
            src={
              process.env.REACT_APP_UPLOAD_BASE_URL + invoice?.restaurant?.logo
            }
            alt="Restaurant Logo"
            className="max-w-full h-10 object-contain rounded-full"
          />

          <p className="text-base font-bold mt-3 mb-6">
            {invoice?.restaurant?.name}
          </p>

          <div className="w-full space-y-1">
            <div className="flex justify-between items-center w-full text-sm text-PrimaryGray">
              <p>Your order number is </p>
              <p className="text-black font-bold underline">
                {invoice?.order_number}
              </p>
            </div>

            {type === "user" && invoice?.user && (
              <div className="w-full flex justify-between items-center text-PrimaryGray">
                <p>Customer Name</p>
                <p className="font-medium">
                  {invoice?.user?.firstName + " " + invoice?.user?.lastName}
                </p>
              </div>
            )}

            <div className="flex justify-between items-center w-full text-sm text-PrimaryGray">
              <p>
                Date :{" "}
                <span className="font-medium">
                  {formatInvoiceDate(invoice?.order_date)}
                </span>
              </p>
              <p>
                Time :{" "}
                <span className="font-medium">
                  {formatInvoiceTime(invoice?.order_time)}
                </span>
              </p>
            </div>

            <div className="flex justify-between items-center w-full text-sm text-PrimaryGray">
              <p className="flex gap-1 items-center w-full text-sm text-PrimaryGray">
                Location :{" "}
                <span className="font-medium">{invoice?.branch_name}</span>
              </p>
              <p className="text-PrimaryGray text-nowrap">
                Invoice :{" "}
                <span className="font-medium">#{invoice?.check_number}</span>
              </p>
            </div>
          </div>

          <div className="w-full text-PrimaryGray font-semibold mt-2 border-t border-b border-lightGray py-1 grid grid-cols-3 uppercase">
            <p>item</p>
            <p className="text-center">qty</p>
            <p className="text-right">total</p>
          </div>

          <div className="w-full text-PrimaryGray border-b border-lightGray">
            {invoice?.order_items.map(
              (el: {
                item_name: string;
                item_price: number;
                item_quantity: number;
                total_price: number;
              }) => (
                <div className="grid grid-cols-3 py-2" key={el.item_name}>
                  <p className="font-normal">{el?.item_name}</p>
                  <p className="text-center">{el?.item_quantity}</p>
                  <div className="flex gap-1 justify-end items-center text-right text-primaryText">
                    {el?.total_price?.toFixed(2)}
                    <img src={SaudiRiyal} className="w-3" alt="ر.س" />
                  </div>
                </div>
              )
            )}
          </div>

          <div className="w-full text-PrimaryGray space-y-2 py-2">
            <div className="flex justify-between">
              <p>Payment Method</p>
              <p className="text-primaryText">{invoice?.payment_method}</p>
            </div>
            <div className="flex justify-between">
              <p>Sub Total</p>
              <div className="flex gap-1 justify-end items-center text-right text-primaryText">
                {invoice?.sub_total_price?.toFixed(2)}
                <img src={SaudiRiyal} className="w-3" alt="ر.س" />
              </div>
            </div>
            <div className="flex justify-between">
              <p>Discount</p>
              <div className="flex gap-1 justify-end items-center text-right text-primaryText">
                {invoice?.discount_amount?.toFixed(2)}
                <img src={SaudiRiyal} className="w-3" alt="ر.س" />
              </div>
            </div>
            <div className="text-primaryText font-bold flex justify-between pb-4">
              <p>Takeout Total (Incl. Tax)</p>
              <div className="flex gap-1 justify-end items-center text-right text-primaryText">
                {invoice?.total_price?.toFixed(2)}
                <img src={SaudiRiyal} className="w-3" alt="ر.س" />
              </div>
            </div>

            {/* Ticket edges */}
            <div
              className={`${
                type === "user"
                  ? "bottom-[28rem]"
                  : type === "search"
                  ? "bottom-[23rem]"
                  : "bottom-[27rem]"
              } absolute -left-3 w-8 h-14 z-10 pointer-events-none`}
            >
              <div className="w-full h-full rounded-r-full bg-gradient-to-l from-black/10 to-white" />
            </div>
            <div
              className={`${
                type === "user"
                  ? "bottom-[28rem]"
                  : type === "search"
                  ? "bottom-[23rem]"
                  : "bottom-[27rem]"
              } absolute -right-3 w-8 h-14 z-10 pointer-events-none`}
            >
              <div className="w-full h-full rounded-l-full bg-gradient-to-r from-black/10 to-white" />
            </div>
            {/* QR Code */}
            {invoice?.receipt_qr && (
              <div className="flex justify-center pt-10">
                <QRCodeCanvas
                  value={invoice?.receipt_qr}
                  size={200}
                  includeMargin={true}
                />
              </div>
            )}
            {(invoice?.isWarranty ||
              invoice?.isRecovery ||
              invoice?.isReplacement) && (
              <div className="pt-4 pb-1">
                <div className="w-full text-primaryText font-semibold border border-lightGray p-4 rounded-lg">
                  <div className="flex items-center gap-1 pb-2">
                    <img src={shield} className="w-5" alt="Terms" />
                    <p>Terms and Polices</p>
                  </div>

                  <ul className="pl-6 space-y-2 marker:text-primaryText font-normal text-xs list-inside list-disc">
                    {invoice?.isWarranty && (
                      <li className="text-gray-400">
                        <span className="text-primaryText">Warranty :</span>{" "}
                        Warranty has {invoice?.warrantyReminderDays}{" "}
                        {invoice?.warrantyReminderDays > 1 ? "days" : "day"}{" "}
                        remaining.
                      </li>
                    )}
                    {invoice?.isRecovery && (
                      <li className="text-gray-400">
                        <span className="text-primaryText">Return :</span>{" "}
                        Return has {invoice?.recoveryReminderDays}{" "}
                        {invoice?.recoveryReminderDays > 1 ? "days" : "day"}{" "}
                        remaining.
                      </li>
                    )}
                    {invoice?.isReplacement && (
                      <li className="text-gray-400">
                        <span className="text-primaryText">Exchange :</span>{" "}
                        Exchange has {invoice?.replacementReminderDays}{" "}
                        {invoice?.replacementReminderDays > 1 ? "days" : "day"}{" "}
                        remaining.
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            )}

            <p className="text-center text-gray-800 text-sm font-medium">
              THANK YOU!
            </p>
            <div className="flex justify-center items-center pt-1 gap-2 select-none">
              <p className="font-medium text-PrimaryGray/60">Powered by</p>
              <img src="/splash-logo.png" alt="Logo" className="h-5 mt-1" />
            </div>

            <div className="w-full text-muted text-xs text-center pb-1">
              {invoice.user || type === "cashier" ? "Saved at:" : "Scanned at:"}{" "}
              {formatDate(new Date(invoice?.createdAt))}
            </div>

            {type === "web" && onSave && (
              <Button
                className="w-full !my-4 !bg-primary !text-white !font-bold focus:outline-none focus:ring-0 hover:!bg-primary/80"
                onClick={() => (type === "web" ? onSave() : null)}
              >
                Save Invoice
              </Button>
            )}

            {type === "cashier" && restaurantId && cashierId && (
              <SaveInvoiceFromCashier
                restaurantId={restaurantId}
                cashierId={cashierId}
                setSelectedCashier={setSelectedCashier}
              />
            )}

            {type === "user" && (
              <div className="flex flex-col items-center gap-2 pb-2">
                <p className="text-sm font-semibold text-center text-gray-600">
                  Download the app
                </p>
                <div className="flex gap-4">
                  <a
                    href="https://play.google.com/store/apps/details?id=your.app.id"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <FaGooglePlay className="text-2xl" />
                  </a>
                  <a
                    href="https://apps.apple.com/us/app/specter-app/id1667869308"
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <FaAppStoreIos className="text-2xl" />
                  </a>
                </div>
              </div>
            )}
          </div>
        </motion.div>
        {/* Bottom cuts */}
        <div className="absolute bottom-0 left-0 z-10 pointer-events-none flex w-full justify-between px-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="w-10 h-8 rounded-t-full translate-y-1/2 bg-white shadow-inner"
            />
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default RestaurantInvoice;
